import { useCallback, useEffect, useMemo, useState, type ReactNode, type SetStateAction } from "react";
import { useSearchParams } from "react-router";
import { getParsedStorageItem } from "common/src/utils/localStorageUtils";
import type { FilterValues, SortConfig as UrlSortConfig } from "features/datagrid-configs/types";
import { DatagridFilters } from "features/datagrid/DatagridFilters";
import { readTableStateFromUrl } from "features/datagrid/hooks/tableUrlState";
import { useTableUrlSync } from "features/datagrid/hooks/useTableUrlSync";
import { normalizeVideoUploadSortField, type VideoTable } from "features/videoUploads/metadata/types";
import { _ } from "i18n";
import client from "libs/client";
import DatagridV2, { type PageSizeOption, type SimpleColumn } from "ui-kit/src/Datagrid/DatagridV2";
import TableSkeleton from "ui-kit/src/Skeleton/TableSkeleton";
import { useAuthStore } from "app/store";
import { buildVideoUploadApiFilters } from "./buildVideoUploadApiFilters";
import { ColumnVisibilityMenu } from "./ColumnVisibilityMenu";
import { type Preset, getColumns, type SortConfig, stickyMinWidths } from "./columns";
import { getVideoUploadSavedByFilterOptions } from "./getVideoUploadSavedByFilterOptions";
import { useVideoUploadsQuery } from "./useVideoUploadsQuery";
import {
	VideoUploadsFilterField,
} from "./videoUploadsFilterConstants";
import { isVideoUploadSortDirection } from "./videoUploadSortFields";
import { getVideoUploadsFilters, shouldClearVeoSourceFilters, veoSourceFilterFieldIds } from "./videoUploadsFilters";

export interface BaseTableSelection {
	selectedIds: ReadonlySet<number>;
	toggle: (id: number) => void;
	setMany: (ids: number[], selected: boolean) => void;
	clear: () => void;
	isSelectable?: (row: VideoTable) => boolean;
	unselectableReason?: (row: VideoTable) => string;
}

function selectionCol(selection: BaseTableSelection, pageRows: VideoTable[]): SimpleColumn<VideoTable> {
	const isSelectable = (row: VideoTable) => selection.isSelectable?.(row) ?? true;
	const selectablePageRowIds = pageRows.filter(isSelectable).map((row) => row.id);
	const allPageRowsSelected =
		selectablePageRowIds.length > 0 && selectablePageRowIds.every((id) => selection.selectedIds.has(id));

	return {
		id: "select",
		key: "actions",
		minWidth: stickyMinWidths.select,
		sticky: true,
		header: () => (
			<input
				className="bulkSelectCheckbox"
				type="checkbox"
				checked={allPageRowsSelected}
				disabled={selectablePageRowIds.length === 0}
				onChange={() => selection.setMany(selectablePageRowIds, allPageRowsSelected === false)}
				aria-label="Select all videos on this page"
			/>
		),
		cell: (value, row) => {
			const selectable = isSelectable(row);

			return (
				<input
					className="bulkSelectCheckbox"
					type="checkbox"
					checked={selection.selectedIds.has(row.id)}
					disabled={selectable === false}
					title={selectable === false ? selection.unselectableReason?.(row) : undefined}
					onChange={() => selection.toggle(row.id)}
					aria-label={`Select video ${row.id}`}
				/>
			);
		},
	};
}

interface BaseTableProps {
	preset?: Preset;
	actions?: SimpleColumn<VideoTable>;
	selection?: BaseTableSelection;
	toolbar?: ReactNode;
}

export function BaseTable({
	preset = "",
	actions,
	selection,
	toolbar,
}: BaseTableProps) {
	const { user } = useAuthStore();
	const defaultPageSize = 10;
	const [searchParams] = useSearchParams();
	const initialUrlState = readTableStateFromUrl(searchParams);
	const filtersCollapsedKey = `videoUploads.filtersCollapsed.${preset === "" ? "all" : preset}`;
	const [pagination, setPagination] = useState<{ pageIndex: number; pageSize: number }>(() => ({
		pageIndex: initialUrlState.pagination.pageIndex,
		pageSize: initialUrlState.pageSizeFromUrl ?? defaultPageSize,
	}));
	const columnVisibilityStorageKey = `videoUploads.columnVisibility.${preset === "" ? "all" : preset}`;
	const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() =>
		getParsedStorageItem<Record<string, boolean>>(columnVisibilityStorageKey, {})
	);
	const [filterValues, setFilterValues] = useState<FilterValues>(() => initialUrlState.filterValues);
	const [filtersCollapsed, setFiltersCollapsed] = useState(() => {
		const stored = localStorage.getItem(filtersCollapsedKey);
		return stored === "true";
	});
	const [sortConfig, setSortConfig] = useState<SortConfig>(() => {
		const urlSort = initialUrlState.sort;
		if (urlSort === undefined) {
			return { name: undefined, sort: undefined };
		}
		const direction = urlSort.direction.toLowerCase();
		const normalizedSort = isVideoUploadSortDirection(direction) ? direction : undefined;
		return { name: normalizeVideoUploadSortField(urlSort.field), sort: normalizedSort };
	});

	const urlSort = useMemo((): UrlSortConfig | undefined => {
		if (sortConfig.name === undefined || sortConfig.sort === undefined) {
			return undefined;
		}
		return { field: sortConfig.name, direction: sortConfig.sort.toUpperCase() };
	}, [sortConfig.name, sortConfig.sort]);

	const setUrlSort = useCallback((value: SetStateAction<UrlSortConfig | undefined>) => {
		setSortConfig((previous) => {
			const currentUrlSort =
				previous.name !== undefined && previous.sort !== undefined
					? { field: previous.name, direction: previous.sort.toUpperCase() }
					: undefined;
			const resolved = typeof value === "function" ? value(currentUrlSort) : value;
			if (resolved === undefined) {
				return { name: undefined, sort: undefined };
			}
			const direction = resolved.direction.toLowerCase();
			const normalizedSort = isVideoUploadSortDirection(direction) ? direction : undefined;
			return { name: resolved.field, sort: normalizedSort };
		});
	}, []);

	useTableUrlSync({
		pagination,
		setPagination,
		sort: urlSort,
		setSort: setUrlSort,
		filterValues,
		setFilterValues,
	});

	const videoUploadsFilters = useMemo(() => getVideoUploadsFilters(filterValues), [filterValues]);

	const resetPageIndex = useCallback(() => {
		setPagination((previous) => (previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }));
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: clearing must not re-run when the selection itself changes
	useEffect(() => {
		selection?.clear();
	}, [pagination.pageIndex, pagination.pageSize, filterValues, sortConfig]);

	useEffect(() => {
		localStorage.setItem(filtersCollapsedKey, String(filtersCollapsed));
	}, [filtersCollapsed, filtersCollapsedKey]);

	const { data: uploadedBy } = client.getVideoUploaders.useQuery({ table: preset });

	const uploaderOptions = useMemo(() => {
		const uploaders = uploadedBy?.data.uploaders ?? [];
		const sortedUploaders = [...uploaders];

		if (user?.email) {
			sortedUploaders.sort((a, b) => {
				if (a === user.email) {
					return -1;
				}
				if (b === user.email) {
					return 1;
				}
				return 0;
			});
		}

		return sortedUploaders.map((uploader) => ({
			value: uploader,
			label: uploader,
		}));
	}, [uploadedBy?.data.uploaders, user?.email]);

	const { data, isLoading, error, refetch } = useVideoUploadsQuery({
		preset,
		page: pagination.pageIndex + 1,
		limit: pagination.pageSize,
		order: { name: normalizeVideoUploadSortField(sortConfig.name), sort: sortConfig.sort },
		filters: buildVideoUploadApiFilters(filterValues),
	});

	const videos = (data?.data.videos ?? []) as VideoTable[];

	const savedByOptions = useMemo(
		() => getVideoUploadSavedByFilterOptions(videos, filterValues[VideoUploadsFilterField.SavedBy]),
		[videos, filterValues],
	);

	const dynamicOptions = useMemo(
		() => ({
			[VideoUploadsFilterField.UploadedBy]: uploaderOptions,
			[VideoUploadsFilterField.SavedBy]: savedByOptions,
		}),
		[uploaderOptions, savedByOptions],
	);

	const { error: serverStatusError, isLoading: isCheckingServerStatus } = client.getVideoServerStatus.useQuery({
		enabled: error != null,
		retry: false,
	});

	const handleFilterChange = useCallback((values: FilterValues | ((previousValues: FilterValues) => FilterValues)) => {
		resetPageIndex();
		setFilterValues((previousValues) => {
			const nextValues = typeof values === "function" ? values(previousValues) : values;

			if (shouldClearVeoSourceFilters(previousValues[VideoUploadsFilterField.Source], nextValues[VideoUploadsFilterField.Source])) {
				const clearedVeoFilters = Object.fromEntries(
					veoSourceFilterFieldIds.map((fieldId) => [fieldId, undefined]),
				);

				return {
					...nextValues,
					...clearedVeoFilters,
				};
			}

			return nextValues;
		});
	}, [resetPageIndex]);

	const handleClearFilters = useCallback(() => {
		resetPageIndex();
		setFilterValues({});
	}, [resetPageIndex]);

	const handleToggleFiltersCollapse = useCallback(() => {
		setFiltersCollapsed((previous) => !previous);
	}, []);

	const handleSortConfigChange = useCallback((value: SortConfig) => {
		setSortConfig(value);
		resetPageIndex();
	}, [resetPageIndex]);

	const handleColumnVisibilityChange = useCallback((visibility: Record<string, boolean>) => {
		setColumnVisibility(visibility);
		localStorage.setItem(columnVisibilityStorageKey, JSON.stringify(visibility));
	}, [columnVisibilityStorageKey]);

	const columns = getColumns({
		preset,
		sortConfig,
		setSortConfig: handleSortConfigChange,
		actions,
	});

	if (error) {
		if (isCheckingServerStatus) {
			return <TableSkeleton />;
		}

		if (serverStatusError) {
			return (
				<div>
					Video Servers are currently unavailable - please try again later. If the
					issue persists, contact Video Server Team.
				</div>
			);
		}

		return (
			<div>
				These videos could not be loaded - please try again. If the issue persists, contact Video Server Team.
			</div>
		);
	}

	if (isLoading) {
		return <TableSkeleton />;
	}

	const pageCount = data?.data.pages ?? 1;
	const selectableColumns = selection ? [selectionCol(selection, videos), ...columns] : columns;

	const toggleableColumns = columns
		.filter((column) => column.id !== "actions")
		.map((column) => {
			const id = column.id ?? String(column.key);
			return { id, label: column.label ?? id };
		});

	const pageSizeOptions: PageSizeOption[] = [10, 20, 30, 40, 50];
	if (pageSizeOptions.includes(defaultPageSize) === false) {
		pageSizeOptions.push(defaultPageSize);
	}

	return (
		<>
			<div className="topControls-filters">
				<DatagridFilters
					filters={videoUploadsFilters}
					filterValues={filterValues}
					onFilterChange={handleFilterChange}
					onClear={handleClearFilters}
					isCollapsed={filtersCollapsed}
					onToggleCollapse={handleToggleFiltersCollapse}
					dynamicOptions={dynamicOptions}
				/>
			</div>

			<div className="topControls">
				<ColumnVisibilityMenu
					columns={toggleableColumns}
					visibility={columnVisibility}
					onChange={handleColumnVisibilityChange}
				/>

				<div className="topControls-actions">
					{toolbar}

					<div className="refetchButton" onClick={() => refetch()}>
						<span className="icon-new icon-admin-syc icon-white icon-24" />
						<div className="buttonText">{_("label.refresh")}</div>
					</div>
				</div>
			</div>

			<DatagridV2
				data={videos}
				simpleColumns={selectableColumns}
				noResults="no results"
				pageCount={pageCount}
				pagination={pagination}
				setPagination={setPagination}
				columnVisibility={columnVisibility}
				horizontalTableScroll
				showPageSizeSelector
				pageSizeOptions={pageSizeOptions}
				totalItems={data?.data.total}
			/>
		</>
	);
}
